// Colecciones de la home — capa de API + vocabulario de UI.
//
// En la base esto son las tablas `label` / `label_product` y en el sidebar el ítem sigue
// diciendo "Etiquetas" (no se renombra para no desorientar a quien ya lo conoce), pero para
// el dueño de producto son LAS SECCIONES CURADAS DE LA HOME: el carrusel "Novedades" y los 4
// tiles de categoría. Los textos de las pantallas hablan ese idioma; el modelo de datos, el
// suyo. Este archivo es el único lugar donde conviven los dos.
//
// Sigue el molde de `reservationsApi.js`: thin wrappers sobre fetch + las constantes de UI del
// dominio en el mismo archivo.

import { endSession } from './session';
import { buildPaginationQuery, getPaginatedData } from './pagination';

const config = process.env;

const apiBase = () => config.REACT_APP_BASE_URL + config.REACT_APP_VERSION;

const authHeaders = () => ({
    'access-token': localStorage.getItem('token'),
    'Content-Type': 'application/json',
});

// ── 1002: sesión vencida NO es lo mismo que "tu rol no alcanza" ───────────────────────────────
//
// El backend responde 1002 en los dos casos y con el mismo body (src/app.js: token ausente,
// sesión inexistente, sesión acotada fuera de su allowlist, y también rol efectivo que no está
// en la lista de la ruta). Este archivo trataba todo 1002 como "sesión vencida" y hacía
// localStorage.clear() + salida al login.
//
// El caso que rompe eso es real y no es raro: el rol efectivo se recalcula POR SUCURSAL
// (BranchController.getBranchRole), así que un manager que en la sucursal seleccionada figura
// como cajero recibe 1002 al tocar cualquier cosa de colecciones. Su sesión está PERFECTA — lo
// que no alcanza es el rol en esa sucursal— y sin embargo quedaba deslogueado de golpe, sin
// explicación y perdiendo lo que tuviera a medio hacer en la pantalla.
//
// Como el body no distingue, se pregunta: `session/checkSessionAdmin` es tokenless y contesta
// `isValidToken` para el token que se le mande. Si dice que la sesión vive, el 1002 era de
// permisos y NO se toca la sesión; si dice que no, ahí sí se sale al login. La única llamada
// extra ocurre cuando ya hubo un 1002, o sea nunca en el camino feliz.
const SESSION_EXPIRED_MESSAGE = 'La sesión expiró';
const FORBIDDEN_MESSAGE = 'Tu usuario no tiene permiso para esta operación en la sucursal seleccionada.';
const UNKNOWN_SESSION_MESSAGE = 'No pudimos verificar tu sesión. Recargá la página y, si sigue, volvé a entrar.';

// Un 1002 en una pantalla dispara varias llamadas en paralelo (detalle + candidatos), y no tiene
// sentido preguntar N veces lo mismo: se comparte la consulta en vuelo.
let sessionProbeInFlight = null;

// true = la sesión vive (el 1002 fue de permisos)
// false = la sesión no sirve más (token vencido, inválido o ausente)
// null = no se pudo averiguar (el probe no contestó): NO se destruye nada, que es el lado que no
//        le hace perder el trabajo a nadie.
async function probeSessionAlive() {
    const token = localStorage.getItem('token');
    if (!token) {
        // Sin token no hay sesión que preservar: es el caso claro de "andá a loguearte".
        return false;
    }

    if (sessionProbeInFlight == null) {
        sessionProbeInFlight = (async () => {
            try {
                const res = await fetch(`${apiBase()}session/checkSessionAdmin`, {
                    method: 'POST',
                    headers: authHeaders(),
                });
                const body = await res.json();
                if (body && Number(body.error) === 0 && body.data) {
                    return !!body.data.isValidToken;
                }
                return false;
            } catch (probeError) {
                return null;
            } finally {
                sessionProbeInFlight = null;
            }
        })();
    }

    return sessionProbeInFlight;
}

function defaultMessageFor(status) {
    if (status === 404) return 'No encontramos esa colección.';
    if (status === 409) return 'La operación no está permitida sobre esta colección.';
    if (status === 403) return 'No tenés permiso para esta operación.';
    return 'Ocurrió un error procesando la solicitud';
}

function errorMessage(body, code, status) {
    const data = body && body.data;

    // Convención del repo: el motivo puntual viaja en `data.message` y el `message` de arriba es
    // el genérico del código.
    if (data && typeof data === 'object' && typeof data.message === 'string') {
        return data.message;
    }

    // 1001 arma `data` como { campo: 'qué está mal' } (invalidRequestResponse). Mostrar eso es
    // bastante mejor que el "Request inválida" pelado. Se limita al 1001 a propósito: otros
    // códigos usan `data` para adjuntar contexto estructurado (p. ej. la lista de placements
    // permitidos), y ahí el mensaje canónico del código es el bueno.
    if (code === 1001 && data && typeof data === 'object') {
        const detalles = Object.values(data).filter((value) => typeof value === 'string');
        if (detalles.length > 0) {
            return detalles.join(' ');
        }
    }

    return (body && body.message) || defaultMessageFor(status);
}

// El backoffice históricamente responde 200 + `{ error: <código>, message }` en el body, pero el
// contrato de colecciones habla de 404 y 409 REALES. Este helper acepta las dos formas y las
// unifica en un throw con `status` (HTTP) y `code` (el del body), así las pantallas preguntan
// por `isNotFound(err)` / `isConflict(err)` y no les importa cuál de las dos llegó.
async function request(path, options = {}) {
    let res;
    try {
        res = await fetch(apiBase() + path, {
            ...options,
            headers: { ...authHeaders(), ...(options.headers || {}) },
        });
    } catch (networkError) {
        const err = new Error('No pudimos hablar con el servidor. Revisá la conexión y reintentá.');
        err.status = 0;
        err.code = 0;
        throw err;
    }

    let body = null;
    try {
        body = await res.json();
    } catch (parseError) {
        body = null;
    }

    const code = body && Number(body.error) > 0 ? Number(body.error) : 0;

    // 1002 = sesión vencida O rol insuficiente (ver el bloque de arriba). Se pregunta cuál de
    // las dos antes de decidir: sólo la primera justifica destruir la sesión, y se sale por
    // `endSession` y no por `homePageURL`, que sólo se escribe en el login y cuando falta manda
    // el browser a "/null" (ver el comentario del catch-all en Router.js).
    if (code === 1002) {
        const alive = await probeSessionAlive();

        const err = new Error(
            alive === true ? FORBIDDEN_MESSAGE
                : alive === false ? ((body && body.message) || SESSION_EXPIRED_MESSAGE)
                    : UNKNOWN_SESSION_MESSAGE
        );
        err.status = res.status;
        err.code = 1002;
        err.body = body;
        err.sessionAlive = alive;

        if (alive === false) {
            endSession();
        }
        throw err;
    }

    if (!res.ok || code) {
        const err = new Error(errorMessage(body, code, res.status));
        err.status = res.status;
        err.code = code;
        err.body = body;
        throw err;
    }

    return body ? body.data : null;
}

// El backend de colecciones manda el status HTTP correcto Y el código de dominio en el body
// (2100 = la colección no existe, 2101 = colección de sistema, no se borra). Se aceptan las dos
// señales: si un proxy se come el status, el body sigue diciendo lo mismo.
export function isNotFound(error) {
    return !!error && (error.status === 404 || error.code === 2100);
}

export function isConflict(error) {
    return !!error && (error.status === 409 || error.code === 2101);
}

// ── Endpoints ────────────────────────────────────────────────────────────────────────────

// Listado paginado. Devuelve ya normalizado { items, total, page, limit, totalPages }.
export async function getCollections(params = {}) {
    const data = await request(`label?${buildPaginationQuery(params)}`);
    return getPaginatedData({ data });
}

// Detalle: { labelInfo, labeledProducts }. `labelInfo` puede venir null contra un backend viejo
// (respondía error:0 con labelInfo:null en vez de 404) — el caller tiene que contemplarlo.
export function getCollection(id) {
    return request(`label/${encodeURIComponent(id)}`);
}

// Crea y devuelve { id, slug } para poder encadenar directo a la curación.
export function createCollection(name) {
    return request('label', { method: 'POST', body: JSON.stringify({ name }) });
}

export function updateCollection(id, patch) {
    return request(`label/${encodeURIComponent(id)}`, { method: 'PATCH', body: JSON.stringify(patch) });
}

export function deleteCollection(id) {
    return request(`label/${encodeURIComponent(id)}`, { method: 'DELETE' });
}

// Alta/baja de UN producto. Idempotentes por contrato: se pueden reintentar sin miedo y no hay
// "guardar" en bloque que pise lo que otro haya tocado mientras tanto.
export function addProductToCollection(id, code) {
    return request(`label/${encodeURIComponent(id)}/product/${encodeURIComponent(String(code))}`, { method: 'PUT' });
}

export function removeProductFromCollection(id, code) {
    return request(`label/${encodeURIComponent(id)}/product/${encodeURIComponent(String(code))}`, { method: 'DELETE' });
}

export async function getCollectionCandidates(id, params = {}) {
    const query = buildPaginationQuery({ page: params.page, limit: params.limit, search: params.search });
    const data = await request(`label/${encodeURIComponent(id)}/candidates?${query}`);
    return getPaginatedData({ data });
}

// ── Vocabulario de UI ────────────────────────────────────────────────────────────────────

export const PLACEMENT_NONE = 'none';

// `placement` es VARCHAR en la base y el vocabulario lo valida la aplicación (ver la migración
// 20260801150000-label-curation-fields), justamente para que sumar un slot no sea un ALTER TABLE.
// Consecuencia para el front: puede llegar un valor que esta lista no conoce, y en ese caso hay
// que MOSTRARLO, no comerlo en silencio (misma decisión que `categories.js` con las categorías
// fuera de vocabulario: un valor invisible se pisa sin querer al guardar).
export const PLACEMENTS = [
    {
        value: PLACEMENT_NONE,
        label: 'Sin ubicación',
        help: 'La colección se cura pero no ocupa ningún lugar de la home. Aunque esté publicada, no se ve.',
    },
    {
        value: 'home_carousel',
        label: 'Carrusel de la home',
        help: 'El carrusel grande de arriba de la home (hoy, "Novedades").',
    },
    {
        value: 'home_tile',
        label: 'Tile de categoría',
        help: 'Uno de los 4 recuadros de categoría de la home. El orden decide cuál va primero.',
    },
];

const PLACEMENT_LABELS = PLACEMENTS.reduce((map, p) => { map[p.value] = p.label; return map; }, {});

export function placementLabel(value) {
    if (value == null || value === '') {
        return placementLabel(PLACEMENT_NONE);
    }
    return PLACEMENT_LABELS[value] || `${value} (fuera de lista)`;
}

export function placementHelp(value) {
    const found = PLACEMENTS.find((p) => p.value === value);
    return found ? found.help : 'Ubicación desconocida para este panel: la definió una versión más nueva del backend.';
}

export function isKnownPlacement(value) {
    return PLACEMENT_LABELS[value] != null;
}

export function isPlaced(value) {
    return !!value && value !== PLACEMENT_NONE;
}

// Slugs de sistema: son los que el storefront pide POR NOMBRE. Borrar uno dejaría una sección de
// la home apuntando a una colección inexistente, así que el backend responde 409. El front los
// conoce para poder explicar el porqué ANTES de que el usuario intente borrar.
export const SYSTEM_SLUGS = ['novedades', 'euro', 'party', 'familiar', 'abstracto'];

// El carrusel de la home. Tiene nombre propio porque no es una colección más: `product.isNew`
// —el campo que el storefront filtra para armar "Novedades"— ES la pertenencia a este slug. La
// ficha de producto lo usa para precargar y para escribir el control "Novedad".
export const NOVEDADES_SLUG = 'novedades';

export function isSystemSlug(slug) {
    return !!slug && SYSTEM_SLUGS.indexOf(String(slug).toLowerCase()) !== -1;
}

// Nombre presentable de una colección a partir de su SLUG.
//
// `GET /product/:code` devuelve `labels` como lista de slugs, no de nombres, y la ficha de
// producto se banca con eso a propósito: pedir además el listado de colecciones agregaría una
// request a cada apertura de ficha —y un modo de falla nuevo— para una línea informativa.
//
// El precio, dicho en voz alta: si el dueño renombra "Euro" a "Eurogames" en el ABM, esta línea
// va a seguir diciendo "Euro". El slug NO cambia con el renombre (por diseño: es lo que el
// storefront pide por nombre), así que lo que se muestra es el nombre canónico de la sección y
// no el editado. Para lo que esta línea tiene que responder — "¿en qué colecciones está este
// producto?" — alcanza; el nombre editado se ve en la pantalla de Colecciones.
const SYSTEM_SLUG_NAMES = {
    novedades: 'Novedades',
    euro: 'Euro',
    party: 'Party',
    familiar: 'Familiares',
    abstracto: 'Abstractos',
};

export function collectionDisplayName(slug) {
    if (!slug) return '';
    const key = String(slug).toLowerCase();
    if (SYSTEM_SLUG_NAMES[key]) {
        return SYSTEM_SLUG_NAMES[key];
    }
    // Colección creada por el usuario: se arma algo legible desde el slug ("juegos-de-mesa" ->
    // "Juegos de mesa") en vez de mostrar el identificador crudo.
    const words = key.replace(/-+/g, ' ').trim();
    return words.charAt(0).toUpperCase() + words.slice(1);
}

// Los slugs que trae `GET /product/:code`, listos para pintar. Tolerante con la forma: contra un
// backend anterior a la Fase 3 el campo no viene, y eso tiene que dar "ninguna" y no reventar.
export function collectionNamesFromLabels(labels) {
    if (!Array.isArray(labels)) return [];
    return labels.filter(Boolean).map(collectionDisplayName);
}

// ── Normalizadores ───────────────────────────────────────────────────────────────────────

function pickFlag(row, keys) {
    for (const key of keys) {
        const value = row[key];
        if (value !== undefined && value !== null) {
            return value === true || value === 1 || value === '1';
        }
    }
    return undefined; // tri-estado: el backend no lo informó
}

function pickValue(row, keys, fallback = null) {
    for (const key of keys) {
        const value = row[key];
        if (value !== undefined && value !== null && value !== '') {
            return value;
        }
    }
    return fallback;
}

export function normalizeCollection(item = {}) {
    return {
        id: item.id,
        name: item.name || '(sin nombre)',
        slug: item.slug || null,
        isPublic: item.is_public === 1 || item.is_public === true || item.is_public === '1',
        placement: item.placement || PLACEMENT_NONE,
        sortOrder: Number(item.sort_order || 0),
        imagePath: item.image_path || '',
        // Contra un backend que todavía no devuelve los contadores quedan en undefined y la UI
        // muestra "—" en vez de un 0 que sería mentira.
        productCount: item.productCount == null ? undefined : Number(item.productCount),
        visibleCount: item.visibleCount == null ? undefined : Number(item.visibleCount),
    };
}

export function normalizeLabeledProduct(row = {}) {
    return {
        code: row.code,
        name: row.name || '(sin nombre)',
        visibleEnWeb: pickFlag(row, ['visibleEnWeb', 'visible']),
        motivo: pickValue(row, ['motivo', 'reason']),
        labeledAt: pickValue(row, ['labeledAt', 'labeled_at', 'created_at']),
    };
}

export function normalizeCandidate(row = {}) {
    const candidate = {
        ...normalizeLabeledProduct(row),
        inCollection: pickFlag(row, ['inLabel', 'inCollection', 'in_collection', 'assigned', 'isAssigned']),
        productType: pickValue(row, ['productType', 'product_type']),
        display: row.display == null ? null : Number(row.display),
    };

    // `/candidates` ya devuelve `visibleEnWeb` y `motivo` por fila, con la misma semántica que el
    // detalle. Lo de abajo queda como RED contra un backend anterior a ese cambio (o un proxy que
    // recorte campos): ahí sólo se puede afirmar lo NEGATIVO, porque `display = 0` es una
    // exclusión dura del catálogo público. Con `display = 1` queda "no sé": el backend puede
    // tener otros criterios y este panel no los conoce — re-implementar acá su regla de
    // publicación es exactamente lo que se desincroniza en silencio la próxima vez que cambie.
    if (candidate.visibleEnWeb === undefined && candidate.display === 0) {
        candidate.visibleEnWeb = false;
        candidate.motivo = candidate.motivo || 'oculto';
    }

    return candidate;
}

// El backend manda motivos cortos ('oculto', 'archivado', 'no existe', …). Se traducen a una
// frase que le diga al dueño QUÉ HACER, con fallback al texto crudo: si mañana aparece un motivo
// nuevo se muestra tal cual en vez de desaparecer de la pantalla.
const MOTIVO_LABELS = {
    'no existe': 'El producto ya no está en el catálogo: sacalo de la colección.',
    'archivado': 'Está archivado. Desarchivalo desde Productos para que se vea.',
    'no es retail': 'No es un producto de catálogo (gastronomía o insumo).',
    'oculto': 'Está marcado como oculto. Publicalo desde Productos para que se vea.',
    'no se publica': 'No cumple los criterios de publicación de la tienda.',
};

export function motivoText(motivo) {
    if (!motivo) return null;
    return MOTIVO_LABELS[motivo] || motivo;
}

// Contadores de una colección, con fallback.
//
// El contrato garantiza `productCount`/`visibleCount` en los items del LISTADO, pero no que
// `labelInfo` del detalle también los traiga. En el detalle sí está la lista completa de
// productos con su `visibleEnWeb`, así que se pueden contar de ahí y no mostrar "—" en una
// pantalla que tiene el dato a la vista. `visibles` queda en null sólo si ni el backend lo dijo
// ni todas las filas informaron su visibilidad: mejor "no sé" que un número inventado.
export function collectionCounts(collection, labeledProducts = []) {
    const asignados = collection && collection.productCount != null
        ? collection.productCount
        : labeledProducts.length;

    let visibles = collection && collection.visibleCount != null ? collection.visibleCount : null;
    if (visibles == null && labeledProducts.every((p) => p.visibleEnWeb !== undefined)) {
        visibles = labeledProducts.filter((p) => p.visibleEnWeb === true).length;
    }

    return { asignados, visibles };
}

// Estado "¿se ve en la web?" listo para pintar. Tri-estado a propósito: "no sabemos" no es "no".
export function visibilityBadge(row) {
    if (row.visibleEnWeb === true) {
        return { text: 'Sí', className: 'badge badge-success', title: 'Se muestra en la tienda.' };
    }
    if (row.visibleEnWeb === false) {
        return {
            text: 'No',
            className: 'badge badge-danger',
            title: motivoText(row.motivo) || 'No se muestra en la tienda.',
        };
    }
    return {
        text: '—',
        className: 'badge badge-light',
        title: 'Se confirma cuando el producto entra a la colección.',
    };
}
